import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import React from 'react';

import { Disposition } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useSaveOfferDisposition } from '../../../../hooks/useCeOffers';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useLagoon } from '../../../../hooks/useLagoon';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import {
  getCurrentMember,
  getEnterpriseId,
} from '../../../../utils/user.utils';
import { mobileOnly } from '../../../ConstantsStyles';
import { CeOffersData } from '../utils';
import { Divider } from './KeywordSearchDropdown.styled';

export const ProgramPromotionList = ({
  programPromotions,
  setSelectedOfferName,
  setIsOfferModalOpen,
}) => {
  const data = useLagoon('ui-messaging')();
  const allConfigs = useLagoon('config');
  const programPromotionContent = find(data, {
    key: ConstantsLagoon.PROGRAM_PROMOTION.PROGRAM_PROMOTION_CONTENT,
  });
  const mobileScreen = useMediaQuery(mobileOnly);
  const isPreEffective = useCurrentMemberIsPreEffective();
  const [, saveOfferDisposition] = useSaveOfferDisposition({});
  const currentMember = getCurrentMember();

  const enableProgramPromotionModalPopup = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PROGRAM_PROMOTION_MODAL_POPUP'
  );

  const handleOfferClick = (e: React.MouseEvent, offer: CeOffersData) => {
    e.preventDefault();

    if (isPreEffective || !offer?.cta?.ctaValue) return;

    const enterpriseId = getEnterpriseId(currentMember);
    if (enableProgramPromotionModalPopup === 'Y') {
      setSelectedOfferName(offer?.offerName);
      setIsOfferModalOpen(true);
    } else {
      if (offer?.offerToken && enterpriseId) {
        saveOfferDisposition({
          variables: {
            enterpriseId,
            disposition: Disposition.ACCEPTED,
            offerToken: offer?.offerToken,
          },
        });
      }

      // Open vendor website directly
      window.open(offer.cta.ctaValue, '_blank');
    }
  };

  return (
    <React.Fragment>
      <Divider />
      <Text
        color="$gray8"
        data-testid="program-promotion-section-heading"
        fontWeight={700}
        size="14px"
      >
        {programPromotionContent?.title}
      </Text>
      {programPromotions.map((offer, idx) => (
        <Flex
          align="start"
          css={{
            gap: '8px',
            '@media (max-width: 600px)': {
              gap: '8px',
            },
          }}
          direction="column"
          key={offer.offerId + idx}
        >
          {offer?.logo && (
            <div
              style={{
                width: mobileScreen ? '60px' : '96px',
                height: mobileScreen ? 'auto' : '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                alt={`${offer.offerName} logo`}
                src={offer.logo}
                style={{
                  height: '25px',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            </div>
          )}
          <Link
            css={{
              'abyss-link-root': {
                fontSize: '16px',
                lineHeight: '20px',
                fontWeight: 700,
                textAlign: 'left',
                width: 'fit-content',
              },
            }}
            data-auto-testid={`program-promotion-${offer?.offerName}-link-text`}
            data-testid={`program-promotion-${offer?.offerName}-link-text`}
            href={offer?.cta?.ctaValue || '#'}
            isDisabled={isPreEffective || !offer?.cta?.ctaValue}
            onClick={(e) => handleOfferClick(e, offer)}
          >
            {offer?.offerName}
          </Link>
          <Text
            color="$gray7"
            css={{
              maxWidth: '564px',
              lineHeight: '18px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
            data-testid={`program-promotion-${offer?.offerName}-description`}
            fontWeight={400}
            size="14px"
          >
            {offer?.offerDescription}
          </Text>
        </Flex>
      ))}
    </React.Fragment>
  );
};
